import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  Checkbox,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as services from "./../../../services/saleng";
import moment from "moment";
import "moment/locale/th";

export default function POSelectionDialog({
  open,
  onClose,
  transaction,
  poSelect = [],
  lockSelect = [],
  onSubmit,
}) {
  const [selectedRadio, setSelectedRadio] = useState("none"); // "none" | "po" | "lock"
  const [checkedItems, setCheckedItems] = useState([]); // ids ของ PO
  const [lockStartDate, setLockStartDate] = useState("");
  const [lockEndDate, setLockEndDate] = useState("");

  // fetch ภายใน dialog
  const [poItems, setPoItems] = useState([]);
  const [lockItems, setLockItems] = useState([]);
  const [lockMasters, setLockMasters] = useState([]);
  const [loadingPO, setLoadingPO] = useState(false);
  const [loadingLock, setLoadingLock] = useState(false);
  const isBranchCustomer =
    transaction?.customerKind === "BRANCH" ||
    String(transaction?.customerKind || "").toUpperCase() === "BRANCH" ||
    transaction?.customer?.customerKind === "BRANCH" ||
    String(transaction?.customer?.customerKind || "").toUpperCase() === "BRANCH" ||
    Boolean(transaction?.customerBranchId ?? transaction?.customer?.customerBranchId);

  const maxSelect = 10;

  // ------------------------
  // ดึงข้อมูลจาก API ภายใน dialog
  // ------------------------
  const fetchPOs = useCallback(async () => {
    try {
      const customerId = transaction?.customerId;
      if (!customerId) {
        setPoItems([]);
        return;
      }
      setLoadingPO(true);
      const resData = await services.getPurchaseOrders({
        customerId,
        currentDate: new Date().toISOString().split("T")[0],
      });
      const openedPOs = resData.filter((po) => po.status !== "close");
      setPoItems(openedPOs);
    } catch (error) {
      console.error("เกิดข้อผิดพลาดในการดึง PO:", error);
      setPoItems([]);
    } finally {
      setLoadingPO(false);
    }
  }, [transaction?.customerId]);

  const fetchLockPricing = useCallback(async () => {
    try {
      if (!isBranchCustomer) {
        setLockItems([]);
        setLockMasters([]);
        return;
      }
      const customerId = transaction?.customerId;
      if (!customerId) {
        setLockItems([]);
        setLockMasters([]);
        return;
      }
      setLoadingLock(true);
      const [masterRes, lockRes] = await Promise.all([
        services.getLockPricingMastersByCustomer(customerId),
        services.getLockPricingByCustomer(customerId),
      ]);
      const activeLocks = (lockRes?.data || []).filter(
        (lock) => !["CLOSED", "CANCELLED"].includes(String(lock?.status || "").toUpperCase()),
      );
      setLockItems(activeLocks);
      setLockMasters(masterRes?.data || []);
    } catch (error) {
      console.error("เกิดข้อผิดพลาดในการดึง lock-pricing:", error);
      setLockItems([]);
      setLockMasters([]);
    } finally {
      setLoadingLock(false);
    }
  }, [transaction?.customerId, isBranchCustomer]);

  // เปิด dialog แล้วค่อยโหลด (และ reload เมื่อ transaction เปลี่ยน)
  useEffect(() => {
    if (!open) return;
    fetchPOs();
    fetchLockPricing();
  }, [fetchLockPricing, fetchPOs, open, transaction]);

  // ตั้งช่วงวันที่เริ่มต้นเป็น Master ที่ส่ง Stock แล้วและเก่าที่สุด
  useEffect(() => {
    if (!open || (lockSelect && lockSelect.length > 0)) return;

    const activeMasterIds = new Set(
      lockItems
        .map((lock) => Number(lock?.masterId ?? lock?.master?.id))
        .filter((id) => Number.isFinite(id) && id > 0),
    );
    const availableMasters = (lockMasters || [])
      .filter(
        (master) =>
          String(master?.status || "").toUpperCase() === "APPROVED" &&
          activeMasterIds.has(Number(master.id)),
      )
      .sort(
        (a, b) =>
          new Date(a.recordDate || 0).getTime() -
          new Date(b.recordDate || 0).getTime(),
      );

    const availableDates = availableMasters
      .map((master) => (master.recordDate ? moment(master.recordDate).format("YYYY-MM-DD") : ""))
      .filter(Boolean);
    setLockStartDate(availableDates[0] || "");
    setLockEndDate(availableDates[0] || "");
  }, [open, lockItems, lockMasters, lockSelect]);

  // sync ค่าเริ่มต้นจาก parent (preselect)
  useEffect(() => {
    if (!open) return;

    if (poSelect && poSelect.length > 0) {
      const ids = (poSelect ?? [])
        .map((po) => po.id || po.purchaseOrderId)
        .filter(Boolean);
      setCheckedItems(ids);
      setSelectedRadio("po");
    } else if (lockSelect && lockSelect.length > 0) {
      setSelectedRadio("lock");
      const selectedLockDate = lockSelect[0]?.recordDate || lockSelect[0]?.master?.recordDate;
      if (selectedLockDate) {
        const date = moment(selectedLockDate).format("YYYY-MM-DD");
        setLockStartDate(date);
        setLockEndDate(date);
      }
    } else {
      setCheckedItems([]);
      setSelectedRadio("none");
    }
  }, [open, poSelect, lockSelect]);

  // Auto select PO ตาม poSelect เมื่อโหลด PO เสร็จ
  useEffect(() => {
    if (!open || poItems.length === 0 || !(poSelect?.length > 0)) return;

    const autoCheckedIds = poItems
      .filter((po) =>
        poSelect.some((s) => (s.id || s.purchaseOrderId) === po.id),
      )
      .map((po) => po.id);

    if (autoCheckedIds.length > 0) {
      setCheckedItems(autoCheckedIds);
      setSelectedRadio("po");
    }
  }, [open, poItems, poSelect]);

  // ------------------------
  // Handlers
  // ------------------------
  const handleToggleCheck = (id, type) => {
    if (type === "po") {
      setCheckedItems((prev) => {
        if (prev.includes(id)) return prev.filter((v) => v !== id);
        if (prev.length < maxSelect) return [...prev, id];
        return prev;
      });
    }
  };

  const handleSelectAll = (type, rows) => {
    const ids = rows.map((r) => Number(r.id));
    if (type === "po") setCheckedItems(ids);
  };

  // ------------------------
  // PO columns/rows
  // ------------------------
  const poColumns = [
    {
      field: "checkbox",
      headerName: "",
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          checked={checkedItems.length === poRows.length && poRows.length > 0}
          indeterminate={
            checkedItems.length > 0 && checkedItems.length < poRows.length
          }
          onChange={(e) =>
            e.target.checked
              ? handleSelectAll("po", poRows)
              : setCheckedItems([])
          }
        />
      ),
      renderCell: (params) => (
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          checked={checkedItems.includes(params.row.id)}
          onChange={() => handleToggleCheck(params.row.id, "po")}
        />
      ),
    },
    { field: "poNumber", headerName: "PO #", width: 200 },
    { field: "detail", headerName: "รายละเอียด", flex: 1 },
    { field: "quantity", headerName: "จำนวนสินค้า", width: 130 },
    { field: "startDate", headerName: "เริ่มต้น", width: 120 },
    { field: "endDate", headerName: "สิ้นสุด", width: 120 },
  ];

  const poRows = useMemo(
    () =>
      (poItems || []).map((t) => {
        const shortPo = t.poNumber?.split("-").pop()?.padStart(3, "0") ?? "-";

        return {
          id: t.id,
          poNumber: shortPo,
          detail: t.detail || "-",
          quantity: `${t.items?.length ?? 0} ไอเท็ม`,
          startDate: t.startDate
            ? moment(t.startDate).add(543, "year").format("D MMM YYYY")
            : "-",
          endDate: t.endDate
            ? moment(t.endDate).add(543, "year").format("D MMM YYYY")
            : "-",
        };
      }),
    [poItems],
  );

  // ------------------------
  // LOCK columns/rows
  // ------------------------
  const availableLockMasters = useMemo(() => {
    const activeCountByMaster = new Map();
    (lockItems || []).forEach((lock) => {
      const masterId = Number(lock?.masterId ?? lock?.master?.id);
      if (!Number.isFinite(masterId) || masterId <= 0) return;
      activeCountByMaster.set(masterId, (activeCountByMaster.get(masterId) || 0) + 1);
    });

    return (lockMasters || [])
      .filter(
        (master) =>
          String(master?.status || "").toUpperCase() === "APPROVED" &&
          activeCountByMaster.has(Number(master.id)),
      )
      .map((master) => ({
        ...master,
        activeItemCount: activeCountByMaster.get(Number(master.id)) || 0,
      }))
      .sort(
        (a, b) =>
          new Date(a.recordDate || 0).getTime() -
          new Date(b.recordDate || 0).getTime(),
      );
  }, [lockItems, lockMasters]);

  const selectedLockRows = useMemo(
    () =>
      (lockItems || []).filter((lock) => {
        const date = moment(lock.recordDate || lock.master?.recordDate).format("YYYY-MM-DD");
        return (
          date &&
          (!lockStartDate || date >= lockStartDate) &&
          (!lockEndDate || date <= lockEndDate) &&
          String(lock.master?.status || "").toUpperCase() === "APPROVED"
        );
      }),
    [lockItems, lockStartDate, lockEndDate],
  );

  // ------------------------
  // Submit
  // ------------------------
  const handleSubmit = () => {
    if (selectedRadio === "po") {
      const selectedPOs = (poItems || []).filter((po) =>
        checkedItems.includes(po.id),
      );
      onSubmit?.({
        type: "po",
        po: selectedPOs,
        lock: [],
        dateRange: null,
      });
      onClose?.();
      return;
    }
    if (selectedRadio === "lock") {
      const selectedLocks = selectedLockRows;
      onSubmit?.({
        type: "lock",
        po: [],
        lock: selectedLocks,
        dateRange: {
          start: lockStartDate || null,
          end: lockEndDate || null,
        },
      });
      onClose?.();
      return;
    }
    // none
    onSubmit?.({
      type: "none",
      po: [],
      lock: [],
      dateRange: null,
    });
    onClose?.();
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => onClose?.()} 
      fullWidth 
      maxWidth="lg"
      PaperProps={{
        style: { borderRadius: "24px" },
        className: "shadow-2xl overflow-hidden bg-slate-50"
      }}
    >
      {/* Header */}
      <div className="bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 z-10">
         <div>
            <h2 className="text-xl font-black text-slate-800">การเลือกราคาสินค้า</h2>
            <p className="text-sm text-slate-400 font-medium mt-0.5">
                เลือกประเภทราคาที่ต้องการใช้กับตั๋วนี้
            </p>
         </div>
      </div>

      <DialogContent className="!p-6 bg-slate-50">
        <div className="flex flex-col gap-4">
            
            {/* Option 1: PO */}
            <div 
                onClick={() => {
                    setCheckedItems([]);
                    setSelectedRadio("po");
                }}
                className={`bg-white rounded-2xl p-5 border-2 cursor-pointer transition-all shadow-sm relative overflow-hidden group ${selectedRadio === 'po' ? 'border-blue-500 ring-4 ring-blue-50' : 'border-transparent hover:border-gray-200'}`}
            >
                <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedRadio === 'po' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                            {selectedRadio === 'po' && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                        </div>
                        <span className={`text-lg font-bold ${selectedRadio === 'po' ? 'text-slate-800' : 'text-slate-600'}`}>
                            ใช้ใบราคาสินค้า (PO)
                        </span>
                    </div>
                    <span className="bg-blue-50 text-blue-600 text-xs font-bold px-3 py-1 rounded-full border border-blue-100">
                        {checkedItems.length}/{maxSelect} รายการ
                    </span>
                </div>

                {loadingPO ? (
                    <div className="text-center py-4 text-slate-400 text-sm">กำลังโหลดข้อมูล...</div>
                ) : (
                    <div className={`mt-3 transition-all ${selectedRadio === 'po' ? 'opacity-100' : 'opacity-50 grayscale'}`}>
                        <div className="bg-slate-50 rounded-xl border border-gray-100 overflow-hidden">
                             <DataGrid
                                rows={poRows}
                                columns={poColumns}
                                autoHeight
                                disableRowSelectionOnClick
                                hideFooter
                                loading={loadingPO}
                                sx={{
                                    border: 'none',
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: '#f8fafc',
                                        color: '#475569',
                                        fontWeight: 'bold',
                                        fontSize: '0.9rem',
                                    },
                                    '& .MuiDataGrid-cell': {
                                        borderBottom: '1px solid #f1f5f9',
                                        color: '#334155',
                                        fontSize: '0.9rem',
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        backgroundColor: '#f1f5f9',
                                    },
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Option 2: Lock Price (เฉพาะ Customer ประเภทสาขา) */}
            {isBranchCustomer && (
            <div 
                onClick={() => {
                    setCheckedItems([]);
                    setSelectedRadio("lock");
                }}
                className={`bg-white rounded-2xl p-5 border-2 cursor-pointer transition-all shadow-sm relative overflow-hidden group ${selectedRadio === 'lock' ? 'border-purple-500 ring-4 ring-purple-50' : 'border-transparent hover:border-gray-200'}`}
            >
                <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedRadio === 'lock' ? 'border-purple-500 bg-purple-500' : 'border-gray-300'}`}>
                            {selectedRadio === 'lock' && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                        </div>
                        <span className={`text-lg font-bold ${selectedRadio === 'lock' ? 'text-slate-800' : 'text-slate-600'}`}>
                            ประกันราคา
                        </span>
                    </div>
                    <span className="bg-purple-50 text-purple-600 text-xs font-bold px-3 py-1 rounded-full border border-purple-100">
                        {availableLockMasters.length} วัน
                    </span>
                </div>
                
                {selectedRadio === "lock" && (
                    <div className="mt-4 animate-fade-in">
                        <div className="flex flex-wrap items-center gap-3 mb-4 bg-purple-50/50 p-3 rounded-xl border border-purple-100">
                          <span className="text-sm font-bold text-slate-500">วันที่ประกันราคา:</span>
                          <input
                            type="date"
                            value={lockStartDate}
                            max={lockEndDate || undefined}
                            onChange={(event) => setLockStartDate(event.target.value)}
                            className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-200"
                          />
                          <span className="text-slate-400 font-bold">ถึง</span>
                          <input
                            type="date"
                            value={lockEndDate}
                            min={lockStartDate || undefined}
                            onChange={(event) => setLockEndDate(event.target.value)}
                            className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-200"
                          />
                        </div>
                        <div className="text-xs text-slate-500">
                          ใช้เฉพาะรายการที่ส่ง Stock แล้วในช่วงวันที่เลือก
                          {loadingLock && " (กำลังโหลด...)"}
                        </div>
                    </div>
                )}
            </div>
            )}

            {/* Option 3: None */}
            <div 
                onClick={() => {
                    setCheckedItems([]);
                    setSelectedRadio("none");
                }}
                className={`bg-white rounded-2xl p-5 border-2 cursor-pointer transition-all shadow-sm relative overflow-hidden group ${selectedRadio === 'none' ? 'border-slate-500 ring-4 ring-slate-100' : 'border-transparent hover:border-gray-200'}`}
            >
                <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedRadio === 'none' ? 'border-slate-500 bg-slate-500' : 'border-gray-300'}`}>
                        {selectedRadio === 'none' && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                    </div>
                    <span className={`text-lg font-bold ${selectedRadio === 'none' ? 'text-slate-800' : 'text-slate-600'}`}>
                        ไม่ใช้การล็อกราคา (ทั่วไป)
                    </span>
                 </div>
            </div>

        </div>
      </DialogContent>

      <div className="p-6 bg-white border-t border-gray-100 flex gap-3 justify-end">
        <button
          onClick={() => onClose?.()}
          className="px-6 py-3 rounded-xl text-slate-500 font-bold hover:bg-slate-50 transition-colors"
        >
          ยกเลิก
        </button>
        <button
          onClick={handleSubmit}
          className="px-8 py-3 bg-slate-800 hover:bg-slate-900 text-white rounded-xl font-bold shadow-lg shadow-slate-200 transition-all transform active:scale-95"
        >
          ยืนยันการเลือก
        </button>
      </div>
    </Dialog>
  );
}
